import { useEffect, useRef, useState } from 'react';
import { drivePreviewUrl, photoRefFor, photoRefName } from '../gmail/emailPhotos';

/**
 * Picking a picture for the body of an email, on Compose or Reply.
 *
 * Holds the files the person chose so the send can attach them inline, and
 * an object URL for each so the preview can show the actual picture. The
 * body refers to a file by name (`photo:beach.jpg`), which is what
 * resolvePhotos matches at send time.
 *
 * The input is rendered by the caller (`PhotoInput`) so it sits in the
 * component tree and can be found by its label in a test.
 */
export function usePhotoPicker() {
  const input = useRef<HTMLInputElement>(null);
  const [photos, setPhotos] = useState<File[]>([]);
  const pending = useRef<((file: File | null) => void) | null>(null);
  const urls = useRef(new Map<string, string>());

  useEffect(() => {
    const held = urls.current;
    return () => {
      for (const url of held.values()) URL.revokeObjectURL(url);
    };
  }, []);

  function pick(): Promise<{ ref: string; caption: string } | null> {
    return new Promise((resolve) => {
      pending.current = (file) => {
        pending.current = null;
        if (file === null) {
          resolve(null);
          return;
        }
        setPhotos((current) =>
          current.some((f) => f.name === file.name) ? current : [...current, file],
        );
        // Keyed by the name as it is WRITTEN into the reference, because that
        // is what previewFor is handed back. Drop 96 started sanitising that
        // name and this map was left on the raw one, so a file with a bracket
        // in it — "Your paragraph text (2).png" — previewed as a grey box.
        const key = photoRefName(file.name);
        if (!urls.current.has(key)) urls.current.set(key, URL.createObjectURL(file));
        resolve(photoRefFor(file));
      };
      const el = input.current;
      if (el === null) {
        pending.current(null);
        return;
      }
      el.value = '';
      el.click();
      // Cancelling the file dialog fires no change event. When focus comes
      // back to the window and nothing was chosen, the pick is over.
      const onFocus = () => {
        window.removeEventListener('focus', onFocus);
        setTimeout(() => pending.current?.(null), 300);
      };
      window.addEventListener('focus', onFocus);
    });
  }

  function previewFor(ref: string): string | null {
    if (ref.startsWith('drive:')) return drivePreviewUrl(ref);
    if (!ref.startsWith('photo:')) return null;
    return urls.current.get(ref.slice('photo:'.length)) ?? null;
  }

  function reset() {
    setPhotos([]);
  }

  const PhotoInput = (
    <input
      ref={input}
      type="file"
      accept="image/*"
      hidden
      aria-label="Add a photo"
      onChange={(event) => {
        const file = event.target.files?.[0] ?? null;
        pending.current?.(file);
      }}
    />
  );

  return { photos, pick, previewFor, reset, PhotoInput };
}
